// Conversation subject derivation (fn-48 R3) — the read side of the
// `metadata.title` convention. Pure function over already-loaded
// data; NEVER triggers a fetch.
//
// Fallback chain:
//   1. `metadata.title` — caller-settable display label, written once
//      at create by the start-conversation flow. String, trimmed,
//      non-empty.
//   2. First NON-bootstrap user message, whitespace-collapsed and
//      truncated to ~60 chars. Callers must already exclude the CP's
//      auto-injected `<system-bootstrap>` message (the reducer flags
//      it `isBootstrap`); this helper defensively rejects it again so
//      internal bootstrap content can never leak into a client-facing
//      subject.
//   3. "Conversation with <agent name>" — agent-name misses fall back
//      to "Assistant". Never an id, never blank.
import { FALLBACK_AGENT_NAME, conversationWith } from '../copy/copy.js';
import { stripLeadingBootstrap } from '../stream/reduce-stream.js';
/** Max subject length when derived from a message (incl. ellipsis). */
export const SUBJECT_MAX_LENGTH = 60;
function truncateSubject(text) {
    if (text.length <= SUBJECT_MAX_LENGTH)
        return text;
    return `${text.slice(0, SUBJECT_MAX_LENGTH - 1).trimEnd()}…`;
}
/**
 * WRITE side of the `metadata.title` convention (fn-48.5/R3): derive
 * the create-time title from the first message when the user supplied
 * no explicit subject. Same whitespace-collapse + truncation as the
 * READ-side message fallback so a created title matches what the list
 * would have derived anyway.
 */
export function titleFromMessage(message) {
    const collapsed = message.replace(/\s+/g, ' ').trim();
    return truncateSubject(collapsed);
}
/** Derive the display subject for a conversation. */
export function deriveSubject(input) {
    const title = input.session.metadata?.title;
    if (typeof title === 'string') {
        const trimmed = title.trim();
        if (trimmed.length > 0)
            return trimmed;
    }
    // fn-72 — grounding rides the first user message as a leading
    // `<system-bootstrap>` block; strip it and derive the subject from the user's
    // real text. A bootstrap-ONLY message (legacy fn-67 turn) strips to empty and
    // falls through to the agent-name fallback, exactly as before.
    if (typeof input.firstUserMessage === 'string') {
        const collapsed = stripLeadingBootstrap(input.firstUserMessage).replace(/\s+/g, ' ').trim();
        if (collapsed.length > 0)
            return truncateSubject(collapsed);
    }
    const agent = typeof input.agentName === 'string' ? input.agentName.trim() : '';
    return conversationWith(agent.length > 0 ? agent : FALLBACK_AGENT_NAME);
}
//# sourceMappingURL=subject.js.map